import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getIndexDBValue,
  setIndexDBValue,
} from "@frontend/utils/indexDBCaching";
import { DEFAULT_WEATHER_PARAMS } from "./constants";
import { FetchWeatherArgs, TFetchedWeatherData } from "./types";
import { paramsToKey } from "./utils";
import { setWeatherData } from "./weatherSlice";

const url = "https://archive-api.open-meteo.com/v1/archive";
/**
 * Fetches weather data from the Open Meteo API and caches it in IndexedDB.
 * If the data is already cached, it retrieves it from IndexedDB instead of making an API call.
 *
 * @param {FetchWeatherArgs} args - The arguments containing the key for caching and the parameters for the API request.
 * @returns {Promise<IWeatherData>} - The weather data.
 */
export const fetchAndCacheWeather = createAsyncThunk(
  "weather/fetchAndCacheWeather",
  async (
    { params = DEFAULT_WEATHER_PARAMS }: FetchWeatherArgs,
    { dispatch },
  ) => {
    const key = paramsToKey("daily", params);

    const stored = await getIndexDBValue(key);
    if (stored?.time?.length) {
      if (!(stored.time || []).every((t: unknown) => typeof t === "string")) {
        const {
          time,
          temperature2mMax,
          precipitationSum,
          relativeHumidity2m,
          ...rest
        } = stored;
        const modified = {
          ...rest,
          time: time.map((t: Date) => t.toISOString()),
          temperature2mMax: Array.from(temperature2mMax),
          precipitationSum: Array.from(precipitationSum),
          relativeHumidity2m: Array.from(relativeHumidity2m),
        };
        dispatch(setWeatherData(modified));
        return modified;
      } else {
        dispatch(setWeatherData(stored));
        return stored;
      }
    }
    const { fetchWeatherApi } = await Promise.resolve(import("openmeteo"));
    const responses = await fetchWeatherApi(url, params);

    // Process first location. Add a for-loop for multiple locations or weather models
    const response = responses[0];

    // Attributes for timezone and location
    const utcOffsetSeconds = response.utcOffsetSeconds();
    // const timezone = response.timezone();
    // const timezoneAbbreviation = response.timezoneAbbreviation();
    // const latitude = response.latitude();
    // const longitude = response.longitude();

    const daily = response.daily()!;

    const weatherData: TFetchedWeatherData = {
      time: [
        ...Array(
          (Number(daily.timeEnd()) - Number(daily.time())) / daily.interval(),
        ),
      ].map((_, i) =>
        new Date(
          (Number(daily.time()) + i * daily.interval() + utcOffsetSeconds) *
            1000,
        ).toISOString(),
      ),
      temperature2mMax: Array.from(daily.variables(0)!.valuesArray()!),
      precipitationSum: Array.from(daily.variables(1)!.valuesArray()!),
      relativeHumidity2m: Array.from(daily.variables(2)!.valuesArray()!),
      ...params,
    };

    await setIndexDBValue(key, weatherData);
    dispatch(setWeatherData(weatherData));
    return weatherData;
  },
);
